import React, { Component } from 'react';
import { connect } from 'react-redux';
import { submit, isInvalid, hasSubmitFailed, formValueSelector } from 'redux-form';
import { push } from 'connected-react-router';
import { createSelector } from 'reselect';
import { bool, func, any, number } from 'prop-types';
import Radium from 'radium';
import { removeAuxiliaryFields } from '@widergy/web-utils/lib/form';
import { isAnyFormLinkVisible, getInitialValues } from '@widergy/utilitygo-forms-web';
import { UTButton, UTLoading, UTLabel } from '@widergy/energy-ui';

import { actionCreators as userFormActions, formPropType, formTypePropType } from 'redux/userFormHandlers';
import { USER_FORM_NAME } from 'constants/reduxForms';
import { accountPropTypes } from 'redux/accountHandlers';
import { ROUTES } from 'constants/routes';
import UTModal from 'app/components/UTModal';
import { message } from 'utils/stringUtils';

import UTForm from './components/UTForm';
import stylesJS from './styles';
import styles from './styles.module.scss';
import CustomFormRenderer from './components/CustomFormRenderer';

class UTUserForm extends Component {
  state = { isDialogOpen: false };

  componentDidMount = () => {
    if (!this.props.form && this.props.formId) {
      this.props.dispatch(
        userFormActions.getForm(
          this.props.formId,
          this.props.formAccount && this.props.formAccount.id,
          this.props.isPublic
        )
      );
      this.handleOpenDialog();
    }
  };

  componentDidUpdate = prevProps => {
    if (prevProps.submitFormLoading && prevProps.submitFormLoading !== this.props.submitFormLoading) {
      if (this.props.useModal) this.handleModalClosed();
      this.props.dispatch(userFormActions.clearForm());
    }
    if (!this.props.form && this.props.formId !== prevProps.formId) {
      this.props.dispatch(
        userFormActions.getForm(this.props.formId, this.props.formAccount && this.props.formAccount.id)
      );
      this.handleOpenDialog();
    }
  };

  componentWillUnmount = () => this.props.dispatch(userFormActions.clearForm());

  onSubmit = values => {
    // called by redux form
    const fields = removeAuxiliaryFields(this.props.form.fields);
    const submitAction = this.props.isPublic ? userFormActions.submitPublicForm : userFormActions.submitForm;
    const options = {
      complaint: this.props.complaint,
      surveyId: this.props.surveyId
    };
    this.props.dispatch(
      submitAction(values, fields, this.props.formType, this.props.accountId, this.props.form, options)
    );
  };

  handleSubmit = () => {
    // called when the submit button is pressed
    this.props.dispatch(submit(USER_FORM_NAME));
  };

  handleModalClosed = () => {
    this.props.handleModalClose();
    this.props.dispatch(userFormActions.clearForm());
  };

  handleCancelForm = () => {
    this.props.dispatch(userFormActions.clearForm());
    if (this.props.isPublic) {
      this.props.dispatch(push(ROUTES.PUBLIC_DASHBOARD));
    } else {
      this.props.dispatch(push(ROUTES.ACCOUNT_STATUS));
    }
  };

  handleOpenDialog = () => this.setState({ isDialogOpen: true });

  handleCloseDialog = () => this.setState({ isDialogOpen: false });

  render() {
    const initialValues = this.props.form ? getInitialValues(this.props.form.fields) : {};

    const acceptButton = this.props.form &&
      !isAnyFormLinkVisible(this.props.registeredFields, this.props.form.fields) && {
        text: message(`Commons:send`),
        onPress: this.handleSubmit,
        disabled: this.props.invalid && this.props.submitFailed
      };

    const descriptionAcceptButton = {
      onPress: this.handleCloseDialog,
      text: message(`Commons:accept`).toUpperCase()
    };

    const Form =
      this.props.form && this.props.form.custom ? (
        <CustomFormRenderer
          form={this.props.form}
          isPublic={this.props.isPublic}
          onSubmit={this.onSubmit}
          onCancel={this.handleCancelForm}
        />
      ) : (
        <UTForm
          userForm={this.props.form}
          onSubmit={this.onSubmit}
          formAccount={this.props.formAccount}
          showTitle={this.props.showTitle}
          isPublic={this.props.isPublic}
          initialValues={initialValues}
          fieldValues={this.props.fieldValues}
          ignoreVisibility={this.props.ignoreVisibility}
          fieldClasses={this.props.fieldClasses}
          fieldTypeProps={this.props.fieldTypeProps}
        />
      );

    const loading = this.props.formLoading || this.props.accountLoading || this.props.submitFormLoading;

    const FormContainer = (
      <UTLoading loading={loading} text={message(`FormCreator:loading`)}>
        {this.props.form &&
          (this.props.useModal ? (
            <div>{Form}</div>
          ) : (
            <div>
              {this.props.form && this.props.form.detailed_description && (
                <UTModal
                  title={this.props.form.name}
                  isOpen={this.state.isDialogOpen}
                  acceptButton={descriptionAcceptButton}
                  onRequestClose={this.handleCloseDialog}
                >
                  <div className={styles.containerModal}>
                    <UTLabel>{this.props.form.detailed_description}</UTLabel>
                  </div>
                </UTModal>
              )}
              {Form}
              {!this.props.form.custom && (
                <div style={stylesJS.buttonContainer}>
                  <UTButton
                    uppercase
                    secondary
                    onPress={this.handleCancelForm}
                    className={styles.submitButton}
                  >
                    {message(`Commons:cancel`)}
                  </UTButton>
                  {this.props.form &&
                    !isAnyFormLinkVisible(this.props.registeredFields, this.props.form.fields) && (
                      <UTButton
                        disabled={this.props.invalid && this.props.submitFailed}
                        onPress={this.handleSubmit}
                        className={styles.submitButton}
                        uppercase
                      >
                        {message(`Commons:send`)}
                      </UTButton>
                    )}
                </div>
              )}
            </div>
          ))}
      </UTLoading>
    );

    const Container = <div style={stylesJS.container}>{FormContainer}</div>;

    if (!this.props.useModal) {
      return Container;
    }
    return (
      <UTModal
        styleContainer={this.props.modalStyle}
        isOpen={this.props.modalIsOpen}
        onRequestClose={this.handleModalClosed}
        title={message(`FormCreator:${this.props.formType}Init`)}
        contentLabel={this.props.formType}
        cancelButton={
          !loading && {
            text: message(`Commons:cancel`),
            onPress: this.handleModalClosed
          }
        }
        acceptButton={!loading && acceptButton}
      >
        <div style={stylesJS.modalForm}>{Container}</div>
      </UTModal>
    );
  }
}

const formAccountSelector = createSelector(
  (store, props) => store.account.accounts.filter(account => account.id === props.accountId)[0],
  store => store.account.currentAccount,
  (account, currentAccount) => account || currentAccount
);
const formSelector = formValueSelector(USER_FORM_NAME);

const mapStateToProps = (store, props) => ({
  form: props.form || store.userForm.form,
  invalid: isInvalid(USER_FORM_NAME)(store),
  submitFailed: hasSubmitFailed(USER_FORM_NAME)(store),
  formAccount: formAccountSelector(store, props),
  accountsLoading: store.account.accountsLoading,
  formLoading: store.userForm.formLoading,
  registeredFields: store.form[USER_FORM_NAME] && store.form[USER_FORM_NAME].registeredFields,
  submitFormLoading: store.userForm.submitFormLoading,
  fieldValues:
    (props.form || store.userForm.form) && (props.form || store.userForm.form).fields.length
      ? formSelector(store, ...(props.form || store.userForm.form).fields.map(field => `id${field.id}`))
      : {}
});

UTUserForm.propTypes = {
  formId: number.isRequired,
  formAccount: accountPropTypes,
  formType: formTypePropType,
  form: formPropType,
  formLoading: bool.isRequired,
  submitFormLoading: bool.isRequired,
  invalid: bool.isRequired,
  submitFailed: bool.isRequired,
  accountId: number,
  useModal: bool,
  showTitle: bool,
  modalIsOpen: bool,
  handleModalClose: func,
  isPublic: bool,
  fieldValues: any.isRequired, // eslint-disable-line react/forbid-prop-types
  registeredFields: any.isRequired, // eslint-disable-line react/forbid-prop-types,
  fieldClasses: any, // eslint-disable-line react/forbid-prop-types,
  fieldTypeProps: any, // eslint-disable-line react/forbid-prop-types
  modalStyle: any, // eslint-disable-line react/forbid-prop-types
  accountLoading: bool,
  complaint: bool,
  ignoreVisibility: bool,
  surveyId: number
};

UTUserForm.defaultProps = {
  registeredFields: {}
};

export default connect(mapStateToProps)(Radium(UTUserForm));
